function normalizeStatusName(name) {
  return String(name || "").trim().toLowerCase();
}

function matchesAny(text, keywords) {
  return keywords.some((word) => text.includes(word));
}

function isSelectedForOnboard(statusName) {
  if (matchesAny(statusName, ["shortlist", "submitted", "new"])) {
    return false;
  }
  return (
    matchesAny(statusName, ["final select", "selected", "offer", "offered", "joining"]) ||
    (statusName.includes("select") && !statusName.includes("shortlist"))
  );
}

/**
 * Derives the recommended next step for a candidate based on status and pipeline counts.
 */
export function resolveCandidateNextAction({
  candidate,
  statusOptions = [],
  interviewCount = 0,
  onboardCount = 0,
}) {
  if (!candidate) return null;

  const statusName = normalizeStatusName(candidate.status_name);
  const statusId =
    candidate.candidate_status_id ??
    candidate.candidate_status_id?.value ??
    "";
  const statusOption = statusOptions.find(
    (opt) => String(opt.value) === String(statusId)
  );
  const schedulesInterview = Number(statusOption?.isScheduled) === 1;
  const hasInterviews = Number(interviewCount) > 0;
  const hasOnboard = Number(onboardCount) > 0;

  if (matchesAny(statusName, ["reject", "declined", "dropped"])) {
    return {
      tone: "secondary",
      icon: "ri-close-circle-line",
      title: "No further action",
      message: "This candidate is marked as rejected. No interview or onboard steps are expected.",
      primaryAction: null,
      secondaryAction: null,
    };
  }

  if (
    hasOnboard ||
    matchesAny(statusName, ["joined", "started", "onboard"])
  ) {
    return {
      tone: "success",
      icon: "ri-building-2-line",
      title: "Onboarding in progress or complete",
      message: "This candidate already has an onboard record or is marked as joined.",
      primaryAction: {
        id: "view_onboards",
        label: "View onboards",
        icon: "ri-external-link-line",
      },
      secondaryAction: null,
    };
  }

  if (isSelectedForOnboard(statusName)) {
    return {
      tone: "primary",
      icon: "ri-user-follow-line",
      title: "Candidate selected — start onboard",
      message: "Capture joining date, compensation, and vendor details in an onboard record.",
      primaryAction: {
        id: "start_onboard",
        label: "Start onboard",
        icon: "ri-user-add-line",
      },
      secondaryAction: hasInterviews
        ? {
            id: "view_interviews",
            label: "View interviews",
            icon: "ri-external-link-line",
          }
        : null,
    };
  }

  if (matchesAny(statusName, ["hold", "await", "pending", "feedback"])) {
    return {
      tone: "warning",
      icon: "ri-time-line",
      title: "Awaiting feedback",
      message: "No immediate action — wait for client or internal decision before changing status.",
      primaryAction: hasInterviews
        ? {
            id: "view_interviews",
            label: "View interviews",
            icon: "ri-external-link-line",
          }
        : null,
      secondaryAction: null,
    };
  }

  if (schedulesInterview || (!hasInterviews && !matchesAny(statusName, ["reject"]))) {
    const roundHint = hasInterviews ? "next interview round" : "first interview";
    return {
      tone: "primary",
      icon: "ri-calendar-check-line",
      title: hasInterviews ? "Continue interview process" : "Schedule an interview",
      message: schedulesInterview
        ? `Current status is ready for scheduling — set up the ${roundHint} for this candidate.`
        : `No interviews recorded yet. Schedule the ${roundHint} when the profile is ready.`,
      primaryAction: {
        id: "schedule_interview",
        label: hasInterviews ? "Schedule next interview" : "Schedule interview",
        icon: "ri-calendar-event-line",
      },
      secondaryAction: hasInterviews
        ? {
            id: "view_interviews",
            label: "View interviews",
            icon: "ri-external-link-line",
          }
        : null,
    };
  }

  if (hasInterviews) {
    return {
      tone: "info",
      icon: "ri-briefcase-line",
      title: "Interviews in progress",
      message: "Review interview outcomes and update candidate status when you have client feedback.",
      primaryAction: {
        id: "view_interviews",
        label: "View interviews",
        icon: "ri-external-link-line",
      },
      secondaryAction: {
        id: "schedule_interview",
        label: "Schedule interview",
        icon: "ri-calendar-event-line",
      },
    };
  }

  return {
    tone: "info",
    icon: "ri-arrow-right-circle-line",
    title: "Update candidate status",
    message: "Move this candidate forward by setting the appropriate status (e.g. submitted, shortlisted, or interview-ready).",
    primaryAction: {
      id: "edit_candidate",
      label: "Edit candidate",
      icon: "ri-edit-line",
    },
    secondaryAction: null,
  };
}

export function buildOnboardPrefillFromCandidate(candidate) {
  if (!candidate?._candidate_id) return null;
  return {
    candidateId: candidate._candidate_id,
    clientId: candidate.candidate_client_id,
    clientName: candidate.client_name,
    requirementId: candidate.candidate_requirement_id,
    requirementName: candidate.requirement_name,
  };
}
